<script>
    import { http } from '@/system/http.js';
    import moment from 'moment';
    import Card from '@/online_booking/new/components/common/Card';
    import StepCounter from '@/online_booking/new/components/common/StepCounter';
    import { mapGetters, mapActions, mapState } from 'vuex';
    import { getQueryParam } from '@/system/helpers';
    import { bookingKeys } from '@/online_booking/new/helpers/constants';

    export default {
        props: {
            permalink: {
                type: String,
                required: true
            }
        },

        data() {
            return {
                first_step: true,
                vouchers: [],
                productGroups: [],
                currentProductGroupSelected: null,
                calendars_count: 0,
                calendar: null,
                voucher: null,
                user: null,
                loading: false
            }
        },

        computed: {
            ...mapState('OnlineBooking', ['currentStep']),
            ...mapGetters('OnlineBooking', ['currentStepNumber', 'totalStepCount', 'getPreviousStepId']),

            isChooseVoucherStepActive () {
                return this.currentStep === 'voucher';
            },

            ungroupedVouchers() {
                return this.vouchers.filter(v => v.product_group_id === 0);
            }
        },

        beforeUnmount() {
            this.$event_bus.$off('LoadData');
            this.$event_bus.$off('CalendarClicked');
            this.$event_bus.$off('booking-restart');
            this.$event_bus.$off('VouchersShowed');
            this.$event_bus.$off('CalendarsShowed');
        },
        mounted() {
            this.$event_bus.$on('LoadData', (data) => {
                this.user = data.user;
                this.first_step = true;
                this.calendars_count = data.calendars_for_online_booking.length;
                this.productGroups = data.product_groups
                    .filter(pg => 'vouchers' in pg)
                    .map(pg => {
                        pg.vouchers.sort((a, b) => {
                            if (a.order > b.order) {
                                return 1;
                            } else if (a.order < b.order) {
                                return -1;
                            }

                            return a.name.localeCompare(b.name);
                        })
                        return pg;
                    }).sort((a, b) => {
                        if (a.order > b.order) {
                            return 1;
                        } else if (a.order < b.order) {
                            return -1;
                        }

                        return a.group_name.localeCompare(b.group_name);
                    });
                this.restart();
            });

            this.$event_bus.$on('booking-restart', () => {
                this.restart();
            });

            this.$event_bus.$on('CalendarsShowed', () => {
                if (!this.first_step) {
                    this.restart();
                }
            });

            this.$event_bus.$on('VouchersShowed', () => {
                this.voucher = null;
            });
        },
        methods: {
            ...mapActions('OnlineBooking', ['setCurrentStep', 'setSelectedValue']),

            restart() {
                if (this.first_step) {
                    this.setCurrentStep('voucher');
                    this.getVouchers();
                    this.currentProductGroupSelected = this.productGroups.length === 1 ? 0 : null;
                }
            },

            preselectVoucher() {
                if (getQueryParam('voucher')) {
                    let voucher_chosen_id = parseInt(getQueryParam('voucher'));
                    const voucher = this.vouchers.find(v => v.id === voucher_chosen_id);
                    if (voucher) {
                        setTimeout(() => {
                            this.voucherClicked(voucher);
                        }, 500);
                    }
                }
            },

            voucherClicked(voucher) {
                this.voucher = voucher;
                this.$store.commit('clearProducts');
                this.$store.commit('addProduct', voucher);
                this.$event_bus.$emit('voucherClicked', voucher);
                this.setSelectedValue({
                    key: bookingKeys.VOUCHER,
                    value: voucher.name
                });
            },

            productGroupClicked(index) {
                if (this.currentProductGroupSelected === index) {
                    this.currentProductGroupSelected = null;
                } else {
                    this.currentProductGroupSelected = index;
                }
            },

            getDateFormatted(date){
                if (date) {
                    return moment(date).locale(moment_locale).format('LL');
                } else {
                    return '';
                }
            },

            getVouchers() {
                this.loading = true;

                http.get('/vouchers/' + this.permalink + '/get')
                    .then(({data}) => {
                        this.vouchers = data.vouchers;
                        this.setCurrentStep('voucher');
                        this.loading = false;
                        this.preselectVoucher();
                    })
                    .catch(({error, errorBag}) => {
                        this.loading = false;
                    });
            },

            voucherPrice(voucher) {
                return voucher.price > 0 
                    ? voucher.price_including_vat_formatted 
                    : trans('global.free');
            }
        },
        components: {
            Card,
            StepCounter,
        },

        watch: {
            isChooseVoucherStepActive (newValue) {
                if (newValue) {
                    window.location.hash = 'choose-voucher';
                    this.$event_bus.$emit('VouchersShowed');
                }
            }
        },
    };

</script>


<template id="choose-voucher">
    <div class="choose-voucher-container" v-if="isChooseVoucherStepActive">
        <card class="p-0 rounded-lg overflow-hidden" id="choose-voucher-panel">
            <!-- Main Section Title -->
            <h4 class="section-title" :class="productGroups.length === 1 ? 'm-b-10' : ''">
                <step-counter :current="currentStepNumber" :total="totalStepCount" />
                {{ trans('vouchers.choose_voucher') }}
            </h4>

            <div
                v-for="(productGroup, index) in productGroups"
                :key="index"
                class="panel no-box-shadow"
                :class="{'overflow-hidden': productGroups.length > 0 && currentProductGroupSelected !== index}"
            >
                <a
                    v-if="productGroups.length > 1"
                    :style="{ 'border-top: 0 !important;' : index === 1}"
                    :id="'headingProduct' + index"
                    class="truncate list-group-item list-group-item-group"
                    role="button"
                    aria-expanded="false"
                    :aria-controls="'collapseVoucher' + index"
                    data-parent="#products-accordion"
                    @click="productGroupClicked(index)"
                >
                    <i class="zmdi zmdi-chevron-right mr-4" v-show="currentProductGroupSelected !== index"></i>
                    <i class="zmdi zmdi-chevron-down mr-4" v-show="currentProductGroupSelected === index"></i>
                    <span>{{ productGroup.group_name }}</span>
                </a>

                <div
                    :id="'collapseVoucher' + index"
                    :aria-labelledby="'headingProduct' + index"
                    class="transition-all duration-75"
                    :class="currentProductGroupSelected === index || (productGroups.length === 1 && ungroupedVouchers.length === 0 ) ? 'max-h-full opacity-1': 'max-h-0 opacity-0'"
                    role="tabpanel"
                >
                    <a
                        v-for="voucher in productGroup.vouchers"
                        class="list-group-item"
                        @click="voucherClicked(voucher)"
                        :key="voucher.index"
                    >
                        <div class="flex flex-column gap-y-2">
                            <div class="flex flex-wrap gap-x-4 gap-y-2 items-center">
                                <h5 class="mt-0 mb-0">{{ voucher.name }}</h5>
                            </div>

                            <div class="product-details-container n-color-text-weak">
                                <div v-if="voucher.product">
                                    <i class="fa-regular fa-clock mr-3"></i>
                                    <span>{{ voucher.product.time_formatted }}</span>
                                    <span>{{ voucherPrice(voucher) }}</span>
                                </div>
                                <div v-else>
                                    <span>{{ voucherPrice(voucher) }}</span>
                                </div>
                            </div>

                            <div class="product-details-container n-color-text-weaker">
                                <span v-if="voucher.product && voucher.product.name">{{ voucher.product.name }}</span>
                                <span v-if="voucher.clips">{{ voucher.clips }} {{ trans('products.voucher_clip') }}</span>
                                <span v-if="voucher.expiry_days">{{ trans('vouchers.expires_after', { days : voucher.expiry_days }) }}</span>
                            </div>
                        </div>
                    </a>
                </div>
            </div>
        </card>
    </div>
</template>

<style lang="scss" scoped>
.product-details-container {
    span:not(:last-child):after {
        content: "\00B7";
        margin-left: 5px;

    }
}
</style>
